import { styled } from '@mui/system';
import { useTheme } from '@mui/material';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Grid2';
import { useEffect, useState } from 'react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { TCustomTheme } from '@mgm/common/theme';
import Chart, { useChart } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { convertToCamelCase } from '@mgm/common/utils';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getDashboardData } from '../../../store/dashboard/selectors';
import { TDashboardOrders } from '../../../types';

const CHART_HEIGHT = 372;
const LEGEND_HEIGHT = 72;

const ChartWrapperStyle = styled('div')(({ theme }) => ({
  height: CHART_HEIGHT,
  marginTop: theme.spacing(5),
  '& .apexcharts-canvas svg': { height: CHART_HEIGHT },
  '& .apexcharts-canvas svg,.apexcharts-canvas foreignObject': {
    overflow: 'visible',
  },
  '& .apexcharts-legend': {
    height: LEGEND_HEIGHT,
    alignContent: 'center',
    borderTop: `solid 1px ${theme.palette.divider}`,
    top: `calc(${CHART_HEIGHT - LEGEND_HEIGHT}px) !important`,
  },
}));

const DashboardTransactionsPie = () => {
  const { transactions } = useAppSelector(getDashboardData);
  const { t } = useTranslation(['common', 'dashboard']);

  const theme = useTheme() as TCustomTheme;

  const getFirstKey = (obj: TDashboardOrders['salesByEmployeePizzaGraph']) => {
    const keys = Object.keys(obj);
    return keys?.[0] || [];
  };

  const [selectedPayment, setSelectedPayment] = useState(getFirstKey(transactions?.incomeByPaymentMethodPieGraph));
  const [selectedCurrency, setSelectedCurrency] = useState(getFirstKey(transactions?.incomeByCurrencyPieGraph));

  useEffect(() => {
    setSelectedPayment(getFirstKey(transactions?.incomeByPaymentMethodPieGraph));
    setSelectedCurrency(getFirstKey(transactions?.incomeByCurrencyPieGraph));
  }, [transactions]);

  const chartOptions = useChart({
    colors: [
      '#FFBD98',
      '#4a599f',
      '#562e5b',
      '#F7D2FF',
    ],
    stroke: { colors: [theme.palette.background.paper] },
    legend: { floating: true, horizontalAlign: 'center' },
    dataLabels: { enabled: true, dropShadow: { enabled: false } },
    tooltip: {
      fillSeriesColor: false,
      y: {
        formatter: (seriesName) => parseFloat(String(seriesName)).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        title: {
          formatter: (seriesName: string) => t(`${seriesName}`, { ns: 'dashboard' }),
        },
      },
    },
    plotOptions: {
      pie: { donut: { labels: { show: false } } },
    },
  });

  const paymentLabels: Array<string> = get(transactions?.incomeByPaymentMethodPieGraph, `${selectedPayment}.labels`, [])?.map((label) => t(`${convertToCamelCase(label)}`, { ns: 'dashboard' }));
  const currencyLabels: Array<string> = get(transactions?.incomeByCurrencyPieGraph, `${selectedCurrency}.labels`, []);

  return (
    <Grid container spacing={3}>
      <Grid size={{
        lg: 6,
        md: 6,
        xs: 12,
        sm: 12,
      }}
      >
        <Card>
          <Grid container>
            <Grid size={{
              lg: 6,
              md: 6,
              xs: 12,
              sm: 6,
            }}
            >
              <CardHeader title={t('salesByCurrency', { ns: 'dashboard' })} />
            </Grid>
          </Grid>
          <ChartWrapperStyle dir="ltr">
            {!currencyLabels.includes('No Data') && !currencyLabels.includes('N/A') && currencyLabels?.length > 0 ? (
              <Chart
                type="pie"
                series={[...get(transactions?.incomeByCurrencyPieGraph, `${selectedCurrency}.values`, [])]}
                options={{
                  ...chartOptions,
                  labels: get(transactions?.incomeByCurrencyPieGraph, `${selectedCurrency}.labels`),
                }}
                height={280}
              />
            ) : <CustomNoRowsOverlay text={t('looksLikeTheresNoDataForTheSelectedMonth')} />}
          </ChartWrapperStyle>
        </Card>
      </Grid>
      <Grid size={{
        lg: 6,
        md: 6,
        xs: 12,
        sm: 12,
      }}
      >
        <Card>
          <Grid container>
            <Grid size={{
              lg: 6,
              md: 6,
              xs: 12,
              sm: 6,
            }}
            >
              <CardHeader title={t('salesByPaymentMethod', { ns: 'dashboard' })} />
            </Grid>
          </Grid>
          <ChartWrapperStyle dir="ltr">
            {!paymentLabels?.includes('No Data') && !paymentLabels.includes('N/A') && paymentLabels?.length > 0 ? (
              <Chart
                type="donut"
                series={[...get(transactions?.incomeByPaymentMethodPieGraph, `${selectedPayment}.values`, [])]}
                options={{
                  ...chartOptions,
                  labels: paymentLabels,
                }}
                height={280}
              />
            ) : <CustomNoRowsOverlay text={t('looksLikeTheresNoDataForTheSelectedMonth')} />}
          </ChartWrapperStyle>
        </Card>
      </Grid>
    </Grid>
  );
};

export {
  DashboardTransactionsPie,
};
